import { apiFetch } from '../../shared/apiClient';

export interface JiraConnectionConfig {
  email: string;
  token: string;
  domain?: string;
  project?: string;
}

export interface JiraConnectionTestResult {
  connected: boolean;
  authenticated: boolean;
  hasProjectAccess?: boolean;
  message: string;
  error?: string;
  user?: {
    accountId: string;
    displayName: string;
    emailAddress: string;
  };
  project?: {
    key: string;
    name: string;
    id: string;
  };
  diagnostic?: {
    status: number;
    domain: string;
    project: string;
  };
}

export class JiraConnectionService {
  /**
   * Проверить подключение к Jira
   */
  static async testConnection(config: JiraConnectionConfig): Promise<JiraConnectionTestResult> {
    const response = await apiFetch('/api/jira/test', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: response.statusText,
      }));
      return {
        connected: false,
        authenticated: false,
        message: 'Connection test failed',
        error: errorData.error || response.statusText,
      };
    }

    const result = (await response.json()) as {
      connected?: boolean;
      success?: boolean;
      authenticated?: boolean;
      hasProjectAccess?: boolean;
      message?: string;
      error?: string;
      user?: JiraConnectionTestResult['user'];
      project?: JiraConnectionTestResult['project'];
      diagnostic?: JiraConnectionTestResult['diagnostic'];
    };

    // Server currently returns `success` (see server/services/jiraService.js); older clients used `connected`.
    const connected = result.connected === true || result.success === true;

    return {
      connected,
      authenticated: result.authenticated === true || connected,
      hasProjectAccess: result.hasProjectAccess === true,
      message:
        result.message ||
        (connected ? 'Connected to Jira successfully.' : 'Connection test failed'),
      error: result.error,
      user: result.user,
      project: result.project,
      diagnostic: result.diagnostic,
    };
  }

  /**
   * Сохранить учетные данные Jira
   */
  static async saveCredentials(config: JiraConnectionConfig): Promise<void> {
    const response = await apiFetch('/api/jira-credentials', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: response.statusText,
      }));
      throw new Error(errorData.error || 'Failed to save Jira credentials');
    }
  }
}
